import React, { useState, useEffect, useRef } from 'react';
import {
  Plus, User, FolderOpen, Palette, Heart, Phone, HelpCircle, ChevronUp,
  Mail, Link, Linkedin, Github, Instagram
} from 'lucide-react';

const ContactPage = () => {
  const email = 'siddharth.khati03@gmail.com';
  const phoneNumber = '+91 7589350003';
  const socialLinks = {
    linkedin: 'https://www.linkedin.com/in/siddharth-singh-khati/',
    github: 'https://github.com/Siddharthkhati/',
    instagram: 'https://www.instagram.com/siddharth.7375/profilecard/?igsh=eXpnbjhiMTR2NHJ6',
  };

  const navItems = [
    { icon: User, label: 'Me', active: false },
    { icon: FolderOpen, label: 'Projects', active: false },
    { icon: Palette, label: 'Skills', active: false },
    { icon: Heart, label: 'Fun', active: false },
    { icon: Phone, label: 'Contact', active: true },
    { icon: HelpCircle, label: '', active: false }
  ];

  const [messages, setMessages] = useState([]);
  const [currentInput, setCurrentInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  const suggestedPrompts = [
    "What's the best way to reach you?",
    "Can I connect with you on LinkedIn?",
    "Show me all your contact options.",
    "What's your GitHub profile?"
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const generateContactResponse = (userPrompt) => {
    let responseContent = "";

    if (userPrompt.includes("all your contact options") || userPrompt.includes("how can i contact you")) {
      responseContent += "Here are all the ways you can reach me:\n\n";
      responseContent += "```card\n";
      responseContent += "title: Contacts\n";
      responseContent += "handle: @SiddharthKhati\n";
      responseContent += `email: ${email}\n`;
      responseContent += `phone: ${phoneNumber}\n`;

      let socialData = [];
      if (socialLinks.linkedin) socialData.push(`linkedin:${socialLinks.linkedin}`);
      if (socialLinks.github) socialData.push(`github:${socialLinks.github}`);
      if (socialLinks.instagram) socialData.push(`instagram:${socialLinks.instagram}`);
      if (socialData.length > 0) responseContent += `social: ${socialData.join(', ')}\n`;

      responseContent += "```\n\n";
      responseContent += "You're welcome to reach out through any of the provided channels—I look forward to connecting with you.";
    } else if (userPrompt.includes("best way to reach you") || userPrompt.includes("email")) {
      responseContent += `The best way to reach me is via email at \`${email}\`. I check it regularly!`;
    } else if (userPrompt.includes("LinkedIn")) {
      responseContent += `Absolutely! You can connect with me on LinkedIn here: \`${socialLinks.linkedin}\`.`;
    } else if (userPrompt.includes("GitHub") || userPrompt.includes("portfolio")) {
      responseContent += `You can check out my projects on GitHub here: \`${socialLinks.github}\`.`;
    } else if (userPrompt.includes("phone number") || userPrompt.includes("call me")) {
      responseContent += `You can reach me on my mobile at \`${phoneNumber}\`.`;
    } else if (userPrompt.includes("Instagram")) {
      responseContent += `You can find me on Instagram here: \`${socialLinks.instagram}\`.`;
    }
    else {
      responseContent += "I'd be happy to share my contact information! You can ask me specific questions like \"What's your email?\" or \"Where can I find you on LinkedIn?\" Or, you can ask \"Show me all your contact options\" for a full list.";
    }
    return responseContent;
  };

  const sendMessage = (promptText) => {
    setMessages((prevMessages) => [...prevMessages, { type: 'user', content: promptText }]);
    setCurrentInput('');
    setIsTyping(true);

    setTimeout(() => {
      const aiResponse = generateContactResponse(promptText);
      setMessages((prevMessages) => [...prevMessages, { type: 'ai', content: aiResponse }]);
      setIsTyping(false);
    }, 2000);
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const autoPromptText = "How can I contact you?";

    let charIndex = 0;
    const typingInterval = setInterval(() => {
      if (charIndex <= autoPromptText.length) {
        setCurrentInput(autoPromptText.substring(0, charIndex));
        charIndex++;
      } else {
        clearInterval(typingInterval);
        sendMessage(autoPromptText);
      }
    }, 50);

    return () => {
      clearInterval(typingInterval);
    };
  }, []);

  const getSocialIcon = (platform) => {
    switch (platform.toLowerCase()) {
      case 'linkedin':
        return <Linkedin className="w-4 h-4 mr-1.5" />;
      case 'github':
        return <Github className="w-4 h-4 mr-1.5" />;
      case 'instagram':
        return <Instagram className="w-4 h-4 mr-1.5" />;
      default:
        return <Link className="w-4 h-4 mr-1.5" />;
    }
  };

  const renderContent = (content) => {
    const cardMatch = content.match(/```card\n([\s\S]*?)\n```/);
    if (cardMatch) {
      const cardContent = cardMatch[1];
      const cardData = {};
      cardContent.split('\n').forEach(line => {
        const [key, value] = line.split(': ').map(s => s.trim());
        if (key && value) {
          cardData[key] = value;
        }
      });

      return (
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 md:p-8 flex flex-col items-start w-full">
          <div className="flex justify-between items-center w-full mb-3 sm:mb-4">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{cardData.title}</h1>
            <span className="text-xs sm:text-sm text-gray-600">{cardData.handle}</span>
          </div>
          {cardData.email && (
            <a href={`mailto:${cardData.email}`} className="text-blue-700 hover:underline text-base sm:text-lg mb-2 sm:mb-3 flex items-center">
              <Mail className="w-4 h-4 mr-1.5" /> {cardData.email}
            </a>
          )}
          {cardData.phone && (
            <a href={`tel:${cardData.phone.replace(/\s/g, '')}`} className="text-blue-700 hover:underline text-base sm:text-lg mb-3 sm:mb-4 flex items-center">
              <Phone className="w-4 h-4 mr-1.5" /> {cardData.phone}
            </a>
          )}
          {cardData.social && (
            <div className="flex flex-wrap gap-x-3 sm:gap-x-4 gap-y-1.5 text-gray-700 text-xs sm:text-sm">
              {cardData.social.split(', ').map((item, idx) => {
                if (!item.trim()) return null;

                const parts = item.split(':');
                if (parts.length < 2) return null;

                const platform = parts[0].trim();
                const link = parts.slice(1).join(':').trim();

                return (
                  <a key={idx} href={link} target="_blank" rel="noopener noreferrer" className="hover:underline flex items-center">
                    {getSocialIcon(platform)} {platform}
                  </a>
                );
              })}
            </div>
          )}
          {content.replace(cardMatch[0], '').trim() && (
            <p className="mt-4 text-gray-700 text-sm sm:text-base leading-normal sm:leading-relaxed">
              {content.replace(cardMatch[0], '').trim()}
            </p>
          )}
        </div>
      );
    }

    return content.split('\n').map((line, lineIndex) => {
      if (line.startsWith('## ')) {
        return <h2 key={lineIndex} className="text-xl sm:text-2xl md:text-3xl font-bold mt-2 sm:mt-3 mb-2 sm:mb-3">{line.substring(3)}</h2>;
      }
      if (line.startsWith('### ')) {
        const match = line.match(/### (.*)/);
        if (match) {
          return (
            <div key={lineIndex} className="flex items-center mt-2 sm:mt-3 mb-1 sm:mb-2">
              <h3 className="text-lg sm:text-xl font-semibold">{match[1]}</h3>
            </div>
          );
        }
      }
      const formattedLine = line.replace(/`(.*?)`/g, '<span class="px-1.5 py-0.5 sm:px-2 sm:py-1 bg-gray-700 text-white text-xs sm:text-sm font-medium rounded-md inline-block mr-0.5 my-0.5 sm:mr-1 sm:my-1">$1</span>');
      return <p key={lineIndex} dangerouslySetInnerHTML={{ __html: formattedLine }} className="leading-normal sm:leading-relaxed text-sm sm:text-base text-justify"></p>;
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 font-sans text-gray-800 flex flex-col text-sm sm:text-base">
      <div className="flex-grow mx-4 sm:mx-8 md:mx-12 lg:mx-[10%] py-6 sm:py-8 md:py-10 lg:py-12 overflow-y-auto">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8 sm:mb-10 md:mb-12 space-y-3 sm:space-y-4">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'} gap-2 sm:gap-3`}
              >
                {message.type === 'ai' && (
                  <img
                    src="images/Siddharth_Singh_Khati.png"
                    alt="Siddharth Singh Khati"
                    className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover self-start mb-1"
                  />
                )}
                <div
                  className={`max-w-[80%] sm:max-w-[70%] py-1.5 px-2.5 sm:py-2 sm:px-3 rounded-2xl sm:rounded-3xl text-sm sm:text-base ${
                    message.type === 'user'
                      ? 'bg-blue-900 text-white'
                      : 'text-gray-800'
                  }`}
                >
                  {renderContent(message.content)}
                </div>
                {message.type === 'user' && (
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-blue-600 flex items-center justify-center text-white self-end mb-1">
                    You
                  </div>
                )}
              </div>
            ))}
            {isTyping && (
              <div className="flex justify-start gap-2 sm:gap-3">
                <img
                  src="images/Siddharth_Singh_Khati.png"
                  alt="Siddharth Singh Khati"
                  className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover self-start mb-1"
                />
                <div className="max-w-[70%] p-3 sm:p-4 rounded-xl bg-white border border-gray-200">
                  <div className="flex items-center space-x-1 sm:space-x-1.5">
                    <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-gray-500 rounded-full animate-bounce delay-100"></span>
                    <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-gray-500 rounded-full animate-bounce delay-200"></span>
                    <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-gray-500 rounded-full animate-bounce delay-300"></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>
      </div>

      <div className="sticky bottom-0 left-0 right-0 bg-gray-50 pt-3 sm:pt-4 border-t border-gray-100">
        <div className="mx-4 sm:mx-8 md:mx-12 lg:mx-[10%] pb-6 sm:pb-8">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3 sm:mb-4 justify-start">
              {suggestedPrompts.map((prompt, index) => (
                <button
                  key={index}
                  onClick={() => sendMessage(prompt)}
                  className="flex items-center px-3 py-1.5 sm:px-4 sm:py-2 bg-gray-200 text-gray-700 rounded-full hover:bg-gray-300 transition-colors text-xs sm:text-sm"
                >
                  <Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-0.5 sm:mr-1" />
                  {prompt}
                </button>
              ))}
            </div>

            <div className="relative">
              <input
                type="text"
                placeholder="Ask me anything"
                className="w-full px-3.5 py-2.5 sm:px-4 sm:py-3 bg-gray-100 border border-gray-200 rounded-full text-sm sm:text-base text-gray-700 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={currentInput}
                readOnly
              />
              <button className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-blue-600 text-white p-1.5 sm:p-2 rounded-full hover:bg-blue-700 transition-colors">
                <ChevronUp className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;